import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {AlertCircle, Building, CheckCircle2, X} from "lucide-react";
import {CompetenciaField} from "../../../shared/ui";

const createInitialForm = () => ({
    empresa_id: '',
    competencia_mes: '',
    competencia_ano: `${new Date().getFullYear()}`,
    incluir_licencas: true,
});

const toMonthLabel = (value) => {
    if (!value) return '';
    const [year, month] = value.split('-');
    if (!year || !month) return value;
    return `${month}/${year}`;
};

export default function FaturaGenerateDialog({open, empresas, faturas = [], onClose, onSubmit, saving = false}) {
    const [form, setForm] = useState(createInitialForm());
    const [errors, setErrors] = useState({});
    const [alertMessage, setAlertMessage] = useState('');

    useEffect(() => {
        if (!open) return;
        setForm(createInitialForm());
        setErrors({});
        setAlertMessage('');
    }, [open]);

    const empresasSelecionaveis = useMemo(() => empresas.filter((empresa) => empresa?.ativo !== false), [empresas]);
    const competenciaPayload = form.competencia_mes && form.competencia_ano ? `${form.competencia_ano}-${form.competencia_mes}-01` : '';
    const empresaSelecionada = useMemo(() => empresas.find((empresa) => empresa.id === form.empresa_id), [empresas, form.empresa_id]);
    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({length: 7}, (_, index) => `${currentYear - 3 + index}`);
    }, []);

    const validate = () => {
        const nextErrors = {};
        let duplicateMessage = '';

        if (!form.empresa_id) nextErrors.empresa_id = 'Selecione uma empresa';
        if (!form.competencia_mes || !form.competencia_ano) nextErrors.competencia_mes = 'Informe mês e ano da competência';

        if (form.empresa_id && competenciaPayload) {
            const duplicate = faturas.find((fatura) => (
                fatura.empresa_id === form.empresa_id && `${fatura.competencia}`.slice(0, 10) === competenciaPayload
            ));

            if (duplicate) {
                duplicateMessage = `Já existe uma fatura para esta competência (${duplicate.status}). Para gerar uma nova, cancele a anterior primeiro.`;
            }
        }

        setErrors(nextErrors);
        setAlertMessage(duplicateMessage);

        return Object.keys(nextErrors).length === 0 && !duplicateMessage;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        onSubmit({
            empresa_id: form.empresa_id,
            competencia: competenciaPayload,
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            sx={{'& .MuiDialog-paper': {borderRadius: 2, maxHeight: '90vh'}}}
        >
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3, borderBottom: '1px solid #e5e7eb'}}>
                <Box>
                    <Typography variant="h5" sx={{color: '#111827', fontWeight: 600}}>
                        Gerar Fatura Manual
                    </Typography>
                    <Typography variant="body2" sx={{color: '#6b7280', mt: 0.5}}>
                        Dispara o motor de faturamento para gerar uma nova fatura
                    </Typography>
                </Box>
                <IconButton onClick={onClose}>
                    <X size={20} color="#6b7280" />
                </IconButton>
            </Box>

            <DialogContent sx={{p: 3}}>
                <Stack spacing={2.5}>
                    <Alert
                        icon={<AlertCircle size={18} />}
                        severity="info"
                        sx={{borderRadius: 2, bgcolor: '#f0fdfa', border: '1px solid #99f6e4', color: '#115e59'}}
                    >
                        <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>Como funciona:</Typography>
                        <Box component="ul" sx={{m: 0, pl: 2.5}}>
                            <Box component="li">O sistema utilizará a lógica automática de faturamento</Box>
                            <Box component="li">Serão incluídos a mensalidade do plano, os opcionais contratados, o desconto e o excedente por métrica</Box>
                            <Box component="li">Não é possível editar manualmente valores após a geração</Box>
                            <Box component="li">Caso necessário, você pode reprocessar a fatura posteriormente</Box>
                        </Box>
                    </Alert>

                    {alertMessage ? (
                        <Alert
                            icon={<AlertCircle size={18} />}
                            severity="error"
                            sx={{borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca'}}
                        >
                            {alertMessage}
                        </Alert>
                    ) : null}

                    <Box>
                        <Typography variant="body2" sx={{color: '#374151', mb: 1}}>
                            Empresa <Box component="span" sx={{color: '#ef4444'}}>*</Box>
                        </Typography>
                        <TextField
                            select
                            value={form.empresa_id}
                            onChange={(event) => setForm((current) => ({...current, empresa_id: event.target.value}))}
                            fullWidth
                            error={Boolean(errors.empresa_id)}
                            helperText={errors.empresa_id}
                            InputProps={{
                                startAdornment: (
                                    <Box sx={{display: 'flex', alignItems: 'center', color: '#9ca3af', mr: 1}}>
                                        <Building size={18} />
                                    </Box>
                                ),
                            }}
                        >
                            <MenuItem value="">Selecione uma empresa</MenuItem>
                            {empresasSelecionaveis.map((empresa) => (
                                <MenuItem key={empresa.id} value={empresa.id}>
                                    {(empresa.razao_social || empresa.nome || empresa.legalName)} {empresa.cnpj ? `(${empresa.cnpj})` : ''}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <CompetenciaField
                        required
                        month={form.competencia_mes}
                        year={form.competencia_ano}
                        years={yearOptions}
                        error={Boolean(errors.competencia_mes)}
                        helperText={errors.competencia_mes || 'Informe o mês e ano de referência da fatura'}
                        onMonthChange={(value) => setForm((current) => ({...current, competencia_mes: value}))}
                        onYearChange={(value) => setForm((current) => ({...current, competencia_ano: value}))}
                    />

                    <Box sx={{bgcolor: '#f9fafb', borderRadius: 2, border: '1px solid #e5e7eb', p: 2}}>
                        <Typography variant="body2" sx={{color: '#374151', fontWeight: 500, mb: 1.5}}>
                            Opções de Geração
                        </Typography>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={form.incluir_licencas}
                                    onChange={(event) => setForm((current) => ({...current, incluir_licencas: event.target.checked}))}
                                />
                            )}
                            label={(
                                <Box>
                                    <Typography variant="body2" sx={{color: '#111827'}}>
                                        Incluir licenças elegíveis para cobrança
                                    </Typography>
                                    <Typography variant="caption" sx={{color: '#6b7280'}}>
                                        O sistema identificará automaticamente quais licenças devem ser incluídas com base no período e regras de cobrança
                                    </Typography>
                                </Box>
                            )}
                            sx={{alignItems: 'flex-start', m: 0}}
                        />
                    </Box>

                    {form.empresa_id ? (
                        <Box sx={{bgcolor: '#f9fafb', borderRadius: 2, border: '1px solid #e5e7eb', p: 2}}>
                            <Typography variant="body2" sx={{color: '#374151', fontWeight: 500, mb: 1.5}}>
                                Preview da Geração
                            </Typography>
                            <Stack spacing={1.25}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CheckCircle2 size={16} color="#0f766e" />
                                    <Typography variant="body2" sx={{color: '#4b5563'}}>
                                        Empresa: <Box component="span" sx={{fontWeight: 600, color: '#111827'}}>{empresaSelecionada?.razao_social || empresaSelecionada?.nome || empresaSelecionada?.legalName || '-'}</Box>
                                    </Typography>
                                </Stack>
                                {form.competencia_mes && form.competencia_ano ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CheckCircle2 size={16} color="#0f766e" />
                                        <Typography variant="body2" sx={{color: '#4b5563'}}>
                                            Competência: <Box component="span" sx={{fontWeight: 600, color: '#111827'}}>{toMonthLabel(`${form.competencia_ano}-${form.competencia_mes}`)}</Box>
                                        </Typography>
                                    </Stack>
                                ) : null}
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CheckCircle2 size={16} color="#0f766e" />
                                    <Typography variant="body2" sx={{color: '#4b5563'}}>
                                        Incluir licenças: <Box component="span" sx={{fontWeight: 600, color: '#111827'}}>{form.incluir_licencas ? 'Sim' : 'Não'}</Box>
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>
                    ) : null}
                </Stack>
            </DialogContent>

            <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 3, pb: 3, pt: 0}}>
                <Button variant="outlined" onClick={onClose} disabled={saving}>
                    Cancelar
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={saving || Boolean(alertMessage)}>
                    Gerar Fatura
                </Button>
            </Box>
        </Dialog>
    );
}
